import React, { useEffect, useState } from "react";
import { Button, Card, CardBody, CardTitle, Col, Collapse, Input, Row, Table } from "reactstrap";
import { MdOutlineExpandLess, MdOutlineExpandMore } from "react-icons/md";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import { ServiceThreadSpinner } from ".";
import { DesktopContainer } from "../layouts";
import { api, constants, dateHelpers } from "../utils";

export default function MobileRwoEdit(props) {
  const navigate = useNavigate();
  const { id } = useParams();
  const ICONS = {
    collapseIcon: (
      <MdOutlineExpandLess className="mobileToggle align-self-center" />
    ),
    expandIcon: (
      <MdOutlineExpandMore className="mobileToggle align-self-center" />
    ),
  };
  const [canEdit, setEditor] = useState(false);
  const [data, setData] = useState({});
  const [expanded, setExpanded] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const sleep = (ms) => new Promise((r) => setTimeout(r, ms));

  useEffect(() => {
    if (!loaded && id) {
      api
        .post(`Repair/GetRepair/${id}`)
        .then((response) => {
          if (response.data.success) {
            setData(response.data.rwo);
            setEditor(response.data.canEdit);
            inputFocus("mechanicInitials");
          } else {
            setData({});
            toast.error(response.data.message);
          }
        })
        .catch((error) => {
          api.catchHandler(error);
          toast.error(error);
        })
        .finally(() => {
          setLoaded(true);
        });
    }
    // below line stops the "React Hook useEffect has missing dependencies" eslint error
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  async function inputFocus(data) {
    await sleep(50); // allows time for input to be recognized
    const el = document.querySelector(`input[name=${data}]`);
    if (el !== null) {
      await el.focus();
    }
  }

  function onChange(field, value) {
    let changes = Object.assign({}, data);
    changes[field] = value;
    setData(changes);
  }

  function save() {
    if (!canEdit) {
      toast.error("This work order is already complete or you are not authorized to make changes");
      return;
    }

    let warnings = [];
    if (!data.mechanicInitials) {
       warnings.push("Tech initials are required\n");
    }
    if (!data.repairComments) {
       warnings.push("Repair comments are required\n");
    }
    if (warnings.length) {
      toast.error(warnings.join(''));
      return;
    }

    api
      .post(`Repair/SaveRepairWorkOrder`, data)
      .then((response) => {
        if (response.data.success) {
          toast.success(`Update of ${data.displayId} was successful!`);
          navigate(constants.ENDPOINTS.mobileRwoSearch);
        } else {
          toast.error(response.data.message);
        }
      })
      .catch(api.catchHandler);
  }

  const toggle = () => {
    setExpanded(!expanded);
    inputFocus("mechanicInitials");
  };

  return (
    <>
      <DesktopContainer screenName="Repair Work Order">
        <ServiceThreadSpinner on={!loaded}>
          {loaded && data ? (
            <div>
              <Card
                id="toggler"
                className="py-0"
                onClick={() => toggle()}
                style={{ cursor: "pointer" }}
              >
                <CardBody className="mobileBlueBg text-white my-0 py-2 px-2">
                  <CardTitle className="mobileBlueBg text-white my-0 py-0 px-0">
                    {expanded ? ICONS.collapseIcon : ICONS.expandIcon}{" "}
                    {data.displayId}
                  </CardTitle>

                  <Collapse
                    className="text-white mx-0 py-0 px-0"
                    toggler="#toggler"
                    isOpen={expanded}
                  >
                    <Table borderless size="sm">
                      <tbody>
                        <tr>
                          <td className="data-cell my-0 py-0" width="80px">
                            <label className="text-black h6 fw-bold my-0">
                              Entered:
                            </label>
                          </td>
                          <td className="data-cell my-0 py-0">
                            <label className="text-white h6 mx-1 my-0">
                              {dateHelpers.dateTimeFormat(
                                data.enteredDate,
                                "YYYY-MM-DD HH:mm"
                              )}
                            </label>
                          </td>
                        </tr>
                        <tr>
                          <td className="data-cell my-0 py-0">
                            <label className="text-black h6 fw-bold my-0">
                              Machine:
                            </label>
                          </td>
                          <td className="data-cell my-0 py-0">
                            <label className="text-white h6 mx-1 my-0">
                              {data.machineName}
                            </label>
                          </td>
                        </tr>
                        <tr>
                          <td className="data-cell my-0 py-0">
                            <label className="text-black h6 fw-bold my-0">
                              &nbsp;
                            </label>
                          </td>
                          <td className="data-cell my-0 py-0">
                            <label className="text-white h6 mx-1 my-0">
                              # {data.machineNumber}
                            </label>
                          </td>
                        </tr>
                        <tr>
                          <td className="data-cell my-0 py-0">
                            <label className="text-black h6 fw-bold my-0">
                              Side:
                            </label>
                          </td>
                          <td className="data-cell my-0 py-0">
                            <label className="text-white h6 mx-1 my-0">
                              {data.machineSide}
                            </label>
                          </td>
                        </tr>
                        <tr>
                          <td className="data-cell my-0 py-0">
                            <label className="text-black h6 fw-bold my-0">
                              Spindle:
                            </label>
                          </td>
                          <td className="data-cell my-0 py-0">
                            <label className="text-white h6 mx-1 my-0">
                              {data.spindle}
                            </label>
                          </td>
                        </tr>
                        <tr>
                          <td className="data-cell my-0 py-0">
                            <label className="text-black h6 fw-bold my-0">
                              Operator:
                            </label>
                          </td>
                          <td className="data-cell my-0 py-0">
                            <label className="text-white h6 mx-1 my-0">
                              {data.operatorInitials}
                            </label>
                          </td>
                        </tr>
                        <tr>
                          <td colSpan="2" className="data-cell my-0 py-0">
                            <label className="text-black h6 fw-bold my-0">
                              Problem:
                            </label>
                          </td>
                        </tr>
                        <tr>
                          <td colSpan="2" className="data-cell my-0 py-0">
                            <label className="text-white h6 mx-1 my-0">
                              {data.problem}
                            </label>
                          </td>
                        </tr>
                        {data.problemChild ? (
                          <tr>
                            <td colSpan="2" className="data-cell my-0 py-0">
                              <label className="text-white h6 mx-1 my-0">
                                {data.problemChild}
                              </label>
                            </td>
                          </tr>
                        ) : null}
                        <tr>
                          <td colSpan="2" className="data-cell my-0 py-0">
                            <label className="text-black h6 fw-bold my-0">
                              Additional Comments:
                            </label>
                          </td>
                        </tr>
                        <tr>
                          <td colSpan="2" className="data-cell my-0 py-0">
                            <label className="text-white h6 mx-1 my-0">
                              {data.comments}
                            </label>
                          </td>
                        </tr>
                      </tbody>
                    </Table>
                  </Collapse>
                </CardBody>
              </Card>

              <Row className="lineheight8">&nbsp;</Row>

              <Card className="py-0">
                <CardBody className="mobileBlueBgAlt py-2 px-2">
                  <CardTitle className="mobileBlueBgAlt py-2 px-2">
                    <Row>
                      <Col xs={12} className="text-white h6 fw-bold py-0 px-1">
                        Mechanics Comments
                      </Col>
                    </Row>
                  </CardTitle>
                  <Table borderless size="sm">
                    <tbody>
                      <tr>
                        <td colSpan="2" className="data-cell my-0 py-0">
                          <label className="text-black h6 fw-bold">
                            <Input
                              name="closed"
                              type="checkbox"
                              className="me-2"
                              checked={data && data.closed}
                              disabled={!canEdit}
                              onChange={(e) => {
                                onChange("closed", e.target.checked);
                              }}
                            />{" "}
                            Repair Complete?
                          </label>
                        </td>
                      </tr>
                      <tr>
                        <td
                          colSpan="2"
                          className="data-cell m-0 p-0 lineheight8"
                        >
                          &nbsp;
                        </td>
                      </tr>
                      <tr>
                        <td className="data-cell my-0 py-0" width="110px">
                          <label className="text-black h6 fw-bold my-0">
                            Tech Initials:
                          </label>
                        </td>
                        <td className="data-cell my-0 py-0">
                          <Input
                            className="w-50"
                            name="mechanicInitials"
                            type="text"
                            maxLength={2}
                            disabled={!canEdit}
                            value={
                              data && data.mechanicInitials
                                ? data.mechanicInitials
                                : ""
                            }
                            onChange={(e) => {
                              onChange("mechanicInitials", e.target.value);
                            }}
                          />
                        </td>
                      </tr>
                      <tr>
                        <td
                          colSpan="2"
                          className="data-cell m-0 p-0 lineheight8"
                        >
                          &nbsp;
                        </td>
                      </tr>
                      <tr>
                        <td colSpan="2" className="data-cell my-0 py-0">
                          <label className="text-black h6 fw-bold mx-1 my-0">
                            Repair Comments:
                          </label>
                        </td>
                      </tr>
                      <tr>
                        <td colSpan="2" className="data-cell my-0 py-0">
                          <Input
                            type="textarea"
                            className="serviceThreadTextArea"
                            name="repairComments"
                            disabled={!canEdit}
                            maxLength={500}
                            value={
                              data && data.repairComments
                                ? data.repairComments
                                : ""
                            }
                            onChange={(e) => {
                              onChange("repairComments", e.target.value);
                            }}
                          />
                        </td>
                      </tr>
                    </tbody>
                  </Table>
                </CardBody>
              </Card>
            </div>
          ) : null}

          <div>
            <Row className="lineheight8">&nbsp;</Row>
            {loaded && data && canEdit ? (
              <Row>
                <Col xs="12">
                  <Button
                    title="Save"
                    className="shredButton w-100 btn-md my-1"
                    onClick={() => save()}
                  >
                    Save
                  </Button>
                </Col>
              </Row>
            ) : null}
            <Row>
              <Col xs="12">
                <Button
                  title="To Search"
                  className="btn w-100 btn-md my-1"
                  onClick={() => navigate(constants.ENDPOINTS.mobileRwoSearch)}
                >
                  Close
                </Button>
              </Col>
            </Row>
          </div>
        </ServiceThreadSpinner>
      </DesktopContainer>
    </>
  );
}
